import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Eye, Edit2, Trash2 } from 'lucide-react';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { FormInput } from '../components/common/FormInput';
import { SelectInput } from '../components/common/SelectInput';
import { Pagination } from '../components/common/Pagination';
import { getCategories, deleteCategory } from '../services/categories';

export const CategoriesPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [filters, setFilters] = useState({ search: '', type: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const data = await getCategories();
        
        // Handle different response structures
        const categoryList = Array.isArray(data) ? data : (data.categories || data.results || []);
        setCategories(categoryList);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load categories');
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleEdit = (categoryId) => {
    navigate(`/categories/${categoryId}/edit`);
  };

  const handleView = (categoryId) => {
    navigate(`/categories/${categoryId}/view`);
  };

  const handleDelete = (categoryId) => {
    setCategoryToDelete(categoryId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;
    setDeleting(categoryToDelete);
    try {
      await deleteCategory(categoryToDelete);
      setCategories(categories.filter(cat => cat.id !== categoryToDelete));
      setError(null);
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete category');
    } finally {
      setDeleting(null);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  // Filter categories based on search and type
  const filteredCategories = categories.filter(cat => {
    const matchesSearch = cat.name.toLowerCase().includes(filters.search.toLowerCase());
    const matchesType = !filters.type || cat.type === filters.type;
    return matchesSearch && matchesType;
  });

  const totalPages = Math.ceil(filteredCategories.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCategories = filteredCategories.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (loading) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">Loading categories...</div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (error) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-red-600">Error: {error}</div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="space-y-6 px-4 sm:px-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Categories</h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">Manage your transaction categories</p>
          </div>
          <Link to="/categories/add" className="w-full sm:w-auto">
            <Button variant="primary" className="w-full sm:w-auto">
              <Plus className="w-5 h-5 mr-2 inline" />
              <span className="hidden sm:inline">Add Category</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </Link>
        </div>

        {/* Search Filter */}
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              type="text"
              name="search"
              placeholder="Search categories..."
              value={filters.search}
              onChange={handleFilterChange}
            />
            <SelectInput
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              options={[
                { label: 'Income', value: 'income' },
                { label: 'Expense', value: 'expense' },
              ]}
              placeholder="Filter by type"
            />
          </div>
        </Card>

        {/* Categories Table/Grid */}
        {filteredCategories.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">
                {categories.length === 0 
                  ? 'No categories found. Create your first category.' 
                  : 'No categories match your search.'}
              </p>
            </div>
          </Card>
        ) : (
          <>
            {/* Desktop View - Table */}
            <Card className="hidden sm:block">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-gray-700 dark:text-gray-300">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <tr>
                      <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-900 dark:text-white">Name</th>
                      <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-900 dark:text-white">Type</th>
                      <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-900 dark:text-white">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCategories.map((category) => (
                      <tr key={category.id} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 sm:px-6 py-4 text-xs sm:text-sm">{category.name}</td>
                        <td className="px-4 sm:px-6 py-4 text-xs sm:text-sm">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            category.type === 'income' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {category.type.charAt(0).toUpperCase() + category.type.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleView(category.id)}
                              className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(category.id)}
                              className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(category.id)}
                              disabled={deleting === category.id}
                              className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded disabled:opacity-50"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <Pagination 
                  currentPage={currentPage} 
                  totalPages={totalPages} 
                  onPageChange={setCurrentPage}
                />
              )}
            </Card>

            {/* Mobile View - Cards */}
            <div className="sm:hidden space-y-3">
              {paginatedCategories.map((category) => (
                <Card key={category.id}>
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{category.name}</h3>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-2 ${
                          category.type === 'income' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {category.type.charAt(0).toUpperCase() + category.type.slice(1)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 border-t dark:border-gray-600 pt-3">
                      <button
                        onClick={() => handleView(category.id)}
                        className="flex-1 p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded text-sm flex items-center justify-center gap-1"
                        title="View"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                      <button
                        onClick={() => handleEdit(category.id)}
                        className="flex-1 p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded text-sm flex items-center justify-center gap-1"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        disabled={deleting === category.id}
                        className="flex-1 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded text-sm flex items-center justify-center gap-1 disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
              {totalPages > 1 && (
                <Pagination 
                  currentPage={currentPage} 
                  totalPages={totalPages} 
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          </>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="max-w-md">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Category</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Are you sure you want to delete this category? This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <Button 
                  variant="danger" 
                  onClick={confirmDelete}
                  disabled={deleting !== null}
                  className="flex-1"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setCategoryToDelete(null);
                  }}
                  disabled={deleting !== null}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
};
